/**
 * Owned Media — detalle de cuenta (§6.1): encabezado fijo con nombre, país,
 * estado, etiqueta N3 y acciones (editar, exportar, historial); indicador de
 * completitud de los 7 bloques (§6.4); pestañas Resumen · Marcas · Stakeholders
 * · Plataformas y accesos · Journeys · Campañas · Experimentos · Proyectos · Way
 * of Working · Reglas y segmentos · Reportes y Links · Equipo; columna derecha
 * de contexto (equipo, links, historial) (§7.1). Los restringidos se muestran
 * con candado y "Solicitar acceso"; abrirlos queda en auditoría (§10.2).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Archive, ArchiveRestore, ArrowLeft, CheckCircle2, Circle, Eye, History, Lock, Pencil, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { EnumBadge } from '@/components/ui/enum-badge';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { MarkdownText } from '@/components/performance/MarkdownText';
import { useOmlAccount, useOmlCommunity, useOmlConfig, useOmlMutations } from '@/hooks/useOml';
import { OML_SCHEMA, OML_TRANSITIONS, ACCOUNT_STATUS, type OmlChildType, type OmlFieldDef } from '../../../../shared/types/oml';
import { formatDateMonth, type Values } from '../../../../shared/oml-rules';
import { EntityForm } from '@/components/owned-media/EntityForm';
import { EntityTable } from '@/components/owned-media/EntityTable';
import { ExportMenu } from '@/components/owned-media/ExportMenu';
import { FieldValue } from '@/components/owned-media/FieldValue';
import { HistoryPanel } from '@/components/owned-media/HistoryPanel';
import { AssetPreview } from '@/components/owned-media/AssetField';
import { FICHA_TONE, TAB_ORDER, entityLabel, entityName, fichaLabel, fieldLabel, statusTone } from '@/components/owned-media/oml-ui';
import { getInitials } from '@/lib/avatar';

const SUMMARY_GROUPS: { key: string; fields: string[] }[] = [
  { key: 'general', fields: ['client', 'countries', 'serviceTypes', 'startDate', 'endDate', 'previousPeriods', 'functionalOwner', 'metricsProvider', 'activeChannels'] },
  { key: 'now', fields: ['currentStatus'] },
  { key: 'business', fields: ['clientSummary', 'mainObjective', 'kpis', 'attributionWindow'] },
  { key: 'commercial', fields: ['deal', 'dealValidUntil', 'contractCaps', 'sowLink', 'capacitySummary'] },
  { key: 'system', fields: ['lastReviewedAt', 'currentStatusUpdatedAt', 'retentionDeadline'] },
];

export function OmlAccountDetailPage() {
  const { t } = useTranslation();
  const { accountId } = useParams<{ accountId: string }>();
  const { data, isLoading, error } = useOmlAccount(accountId);
  const { data: config } = useOmlConfig();
  const { data: members = [] } = useOmlCommunity();
  const m = useOmlMutations();
  const [editing, setEditing] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [showArchived, setShowArchived] = useState(false);
  // ?tab= abre la ficha en una pestaña (lápiz de las vistas transversales, 08/10/2026).
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [tab, setTab] = useState(() => (tabParam && [...TAB_ORDER.map((x) => x.key), 'history'].includes(tabParam) ? tabParam : 'summary'));
  // Navegar a la misma ficha con otro ?tab= (lápiz de otra vista) cambia la pestaña.
  useEffect(() => {
    if (tabParam && [...TAB_ORDER.map((x) => x.key), 'history'].includes(tabParam)) setTab(tabParam);
  }, [tabParam]);
  // Historial solo si la matriz 9.2 se lo da al rol: ?tab=history (deep link, lápiz de otra vista) cae en el resumen.
  const canHistory = data?.access.canSeeHistory !== false;
  useEffect(() => {
    if (!canHistory && tab === 'history') setTab('summary');
  }, [canHistory, tab]);
  const [quickField, setQuickField] = useState<string | null>(null);
  const [addingTeam, setAddingTeam] = useState(false);
  const names = useMemo(() => Object.fromEntries(members.map((x) => [x.email, x.name])), [members]);

  if (isLoading) return <div className="flex justify-center py-20"><WinclapLoader size={48} className="text-foreground/25" /></div>;
  if (error || !data) {
    return (
      <div className="max-w-xl mx-auto mt-10 text-center space-y-3">
        <Lock className="h-8 w-8 mx-auto text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{error?.message ?? t('oml.detail.not_found', 'La cuenta no existe o no tenés acceso.')}</p>
        <Button asChild variant="outline"><Link to="/owned-media"><ArrowLeft className="h-4 w-4 mr-1" />{t('oml.detail.back', 'Volver al listado')}</Link></Button>
      </div>
    );
  }

  const { account, children, access, summary } = data;
  const locked = new Set(account.locked);
  const def = OML_SCHEMA.account;
  const fieldDef = (k: string) => def.fields.find((f) => f.key === k)!;
  const reveal = (field: string) => {
    setRevealed((p) => new Set(p).add(field));
    m.logRestrictedView.mutate({ accountId: account.id, entityType: 'account', field });
  };
  const relationNames: Record<string, string> = {};
  for (const k of Object.keys(children) as OmlChildType[]) for (const e of children[k]) relationNames[e.id] = entityName(k, e as Values);
  const ctx = { accountId: account.id, names, relationNames, locked, type: 'account' as const };
  const rows = (k: OmlChildType) => children[k] as (Values & { id: string; locked: string[]; archived?: boolean })[];
  const canChangeStatus = access.roles.includes('lead') || access.roles.includes('admin');
  // Lápiz por campo (08/10/2026): mismas reglas que el formulario completo (EntityForm.canEditField).
  const canQuickEdit = (k: string): boolean => {
    const f = fieldDef(k);
    if (!access.canEditAccount || account.archived || f.system || locked.has(k)) return false;
    if (access.editableAccountFields === 'none') return false;
    if (access.editableAccountFields !== 'all') return access.editableAccountFields.includes(k);
    if (f.restricted === 'commercial' && !access.canSeeCommercial) return false;
    if (k === 'capacitySummary' && !canChangeStatus) return false;
    return true;
  };
  const canAddTeam = !!access.entities.assignment?.create && !account.archived;
  const team = children.assignment.filter((a) => !a.archived && (!a.to || String(a.to) >= data.today));

  return (
    <div className="space-y-4">
      {/* Encabezado fijo (§7.2) */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-3 bg-background/95 backdrop-blur-sm border-b">
        <div className="flex flex-wrap items-center gap-3">
          <Button asChild variant="ghost" size="icon"><Link to="/owned-media" aria-label={t('oml.detail.back', 'Volver al listado')}><ArrowLeft className="h-4 w-4" /></Link></Button>
          <div className="h-10 w-10 rounded-md border bg-muted flex items-center justify-center overflow-hidden text-sm font-semibold">
            {account.logoAssetId ? <AssetPreview accountId={account.id} assetId={String(account.logoAssetId)} compact /> : getInitials(account.name)}
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold leading-tight truncate">{account.name}</h1>
            <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2">
              <span>{account.client} · {(account.countries ?? []).join(', ')}</span>
              <EnumBadge value={account.status} tone={statusTone(account.status)} />
              <EnumBadge value={summary.fichaStatus} tone={FICHA_TONE[summary.fichaStatus]} label={fichaLabel(t, summary.fichaStatus)} />
              <EnumBadge value="N3" tone="warning" label={t('oml.n3', 'Confidencial de negocio · N3')} />
              {account.archived && <EnumBadge value="archived" tone="muted" label={t('oml.table.archived', 'archivado')} />}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {canChangeStatus && !account.archived && <Button size="sm" variant="outline" onClick={() => setStatusOpen(true)}>{t('oml.detail.change_status', 'Cambiar estado')}</Button>}
            {access.canEditAccount && !account.archived && <Button size="sm" onClick={() => setEditing(true)}><Pencil className="h-4 w-4 mr-1" />{t('common.edit', 'Editar')}</Button>}
            <ExportMenu canExport={access.canExport} scope="account" accountId={account.id} />
            {canHistory && <Button size="sm" variant="ghost" onClick={() => setTab('history')}><History className="h-4 w-4 mr-1" />{t('oml.history.title', 'Historial de cambios')}</Button>}
            {access.canArchive && (account.archived
              ? <Button size="sm" variant="outline" disabled={m.restoreAccount.isPending} onClick={() => m.restoreAccount.mutate({ accountId: account.id })}><ArchiveRestore className="h-4 w-4 mr-1" />{t('oml.table.restore', 'Restaurar')}</Button>
              : <ArchiveAccountButton accountId={account.id} name={account.name} />)}
          </div>
        </div>
      </div>

      {/* Completitud (§6.4) */}
      {summary.completeness.percent < 100 && (
        <Card className="border-amber-200/70 bg-amber-50/40 dark:bg-amber-950/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="text-sm font-semibold">{t('oml.completeness.title', 'Completitud de la ficha')}: {summary.completeness.percent} %</div>
              <div className="h-1.5 flex-1 rounded bg-muted overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${summary.completeness.percent}%` }} /></div>
            </div>
            <ul className="grid gap-1 sm:grid-cols-2 text-sm">
              {summary.completeness.blocks.map((b) => (
                <li key={b.key} className="flex items-center gap-2">{b.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}{t(`oml.completeness.${b.key}`, b.key)}</li>
              ))}
            </ul>
            {summary.completeness.missingRecommended.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">{t('oml.completeness.recommended', 'Recomendados sin cargar')}: {summary.completeness.missingRecommended.map((k) => fieldLabel(t, 'account', k)).join(' · ')}</p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap h-auto">
            {TAB_ORDER.map((tb) => <TabsTrigger key={tb.key} value={tb.key}>{t(`oml.tabs.${tb.key}`, tb.key)}</TabsTrigger>)}
            {canHistory && <TabsTrigger value="history">{t('oml.tabs.history', 'Historial')}</TabsTrigger>}
          </TabsList>

          <TabsContent value="summary" className="space-y-4">
            {SUMMARY_GROUPS.map((g) => (
              <Card key={g.key}>
                <CardHeader className="pb-2"><CardTitle className="text-base">{t(`oml.summary.${g.key}`, g.key)}</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {g.fields.map((k) => {
                    const f = fieldDef(k);
                    const isLocked = locked.has(k);
                    const hidden = f.restricted && !isLocked && !revealed.has(k);
                    return (
                      <div key={k} className="grid gap-1 sm:grid-cols-[220px_1fr] text-sm">
                        <div className="text-muted-foreground flex items-center gap-1">{fieldLabel(t, 'account', k)}{f.restricted && <Lock className="h-3 w-3" />}</div>
                        <div className="min-w-0 group flex items-start gap-1">
                          <div className="min-w-0 flex-1">
                          {hidden
                            ? <Button size="sm" variant="outline" className="h-7" onClick={() => reveal(k)}><Eye className="h-3.5 w-3.5 mr-1" />{t('oml.detail.reveal', 'Mostrar (queda en auditoría)')}</Button>
                            : f.type === 'dateMonth' && !isLocked ? <span>{formatDateMonth(account[k]) || '—'}</span>
                            : <FieldValue def={f as OmlFieldDef} value={account[k]} ctx={ctx} />}
                          </div>
                          {!hidden && canQuickEdit(k) && (
                            <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100" aria-label={t('oml.detail.edit_field', 'Editar {{field}}', { field: fieldLabel(t, 'account', k) })} onClick={() => setQuickField(k)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
            <SummaryBlock onEdit={() => setTab('stakeholders')} title={t('oml.tabs.stakeholders', 'Stakeholders')} items={children.stakeholder.filter((s) => !s.archived && s.active !== false).map((s) => `${s.fullName} · ${s.role}${Array.isArray(s.approves) && s.approves.length ? ` · ${t('oml.stakeholder.approves', 'aprueba')}: ${(s.approves as string[]).join(', ')}` : ''}`)} />
            <SummaryBlock onEdit={() => setTab('platforms')} title={t('oml.tabs.platforms', 'Plataformas y accesos')} items={children.platform.filter((p) => !p.archived && !p.revokedAt).map((p) => `${p.platform} · ${p.usage} · ${p.accessType}${p.isCentral ? ` · ${t('oml.platform.central', 'central')}` : ''}`)} />
            <SummaryBlock onEdit={() => setTab('journeys')} title={t('oml.tabs.journeys', 'Journeys')} items={children.journey.filter((j) => !j.archived).map((j) => `${j.name} · ${j.status}`)} />
            <SummaryBlock onEdit={() => setTab('rules')} title={t('oml.rules.channel_rules', 'Reglas de canal')} items={children.channelRule.filter((r) => !r.archived).map((r) => `${r.channel}${r.dailyLimitPerUser !== null && r.dailyLimitPerUser !== undefined ? ` · ${t('oml.rules.limit', 'máx. {{n}}/día', { n: r.dailyLimitPerUser })}` : ''}${r.frequentTimes ? ` · ${r.frequentTimes}` : ''}`)} />
          </TabsContent>

          {TAB_ORDER.filter((tb) => tb.types.length).map((tb) => (
            <TabsContent key={tb.key} value={tb.key} className="space-y-6">
              {tb.key === 'wow' ? (
                <WayOfWorking rows={rows('process')} detail={data} config={config} members={members} names={names} showArchived={showArchived} />
              ) : tb.types.map((type) => (
                <section key={type} className="space-y-2">
                  {tb.types.length > 1 && <h3 className="font-semibold">{entityLabel(t, type, true)}</h3>}
                  <EntityTable type={type} detail={data} config={config} members={members} rows={rows(type)} showArchived={showArchived} />
                </section>
              ))}
              {access.canSeeArchived && <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch checked={showArchived} onCheckedChange={setShowArchived} />{t('oml.detail.show_archived', 'Mostrar archivados')}</label>}
            </TabsContent>
          ))}

          {canHistory && (
            <TabsContent value="history">
              <HistoryPanel accountId={account.id} names={names} limit={200} />
            </TabsContent>
          )}
        </Tabs>

        {/* Columna de contexto (§7.1 ref. 2) */}
        <aside className="space-y-4">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm">{t('oml.tabs.team', 'Equipo')}</CardTitle>
              <div className="flex gap-1">
                {canAddTeam && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setAddingTeam(true)}><UserPlus className="h-3.5 w-3.5 mr-1" />{t('oml.detail.add_team', 'Sumar')}</Button>}
                <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" aria-label={t('oml.detail.edit_block', 'Ver y editar {{block}}', { block: t('oml.tabs.team', 'Equipo') })} onClick={() => setTab('team')}><Pencil className="h-3.5 w-3.5" /></Button>
              </div>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              {team.length === 0 && <p className="text-muted-foreground">{t('oml.detail.no_team', 'Sin asignaciones vigentes.')}</p>}
              {team.map((a) => <div key={a.id} className="flex justify-between gap-2"><span className="truncate">{String(a.displayName ?? a.email ?? a.externalName)}</span><span className="text-muted-foreground shrink-0">{String(a.role)}</span></div>)}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm">{t('oml.tabs.reports', 'Reportes y links')}</CardTitle>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" aria-label={t('oml.detail.edit_block', 'Ver y editar {{block}}', { block: t('oml.tabs.reports', 'Reportes y links') })} onClick={() => setTab('reports')}><Pencil className="h-3.5 w-3.5" /></Button>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              {children.link.filter((l) => !l.archived).slice(0, 8).map((l) => <a key={l.id} href={String(l.url)} target="_blank" rel="noopener noreferrer" className="block truncate text-primary hover:underline">{String(l.linkType)} · {String(l.name)}</a>)}
              {children.link.filter((l) => !l.archived).length === 0 && <p className="text-muted-foreground">{t('oml.detail.no_links', 'Sin links cargados.')}</p>}
            </CardContent>
          </Card>
          {canHistory && <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">{t('oml.detail.recent_changes', 'Últimos cambios')}</CardTitle></CardHeader>
            <CardContent className="text-xs space-y-1.5">
              {data.history.slice(0, 8).map((h) => <div key={h.id} className="truncate"><span className="text-muted-foreground">{new Date(h.at).toLocaleDateString()}</span> · {names[h.by] ?? h.by} · {h.field === '*' ? entityLabel(t, h.entityType) : fieldLabel(t, h.entityType, h.field)}</div>)}
            </CardContent>
          </Card>}
        </aside>
      </div>

      {editing && <EntityForm open onOpenChange={setEditing} type="account" accountId={account.id} entity={account as Values & { id: string; locked: string[]; fieldVersions: Record<string, string>; updatedAt: string }} detail={data} config={config} members={members} />}
      {quickField && <EntityForm open onOpenChange={(o) => { if (!o) setQuickField(null); }} type="account" accountId={account.id} entity={account as Values & { id: string; locked: string[]; fieldVersions: Record<string, string>; updatedAt: string }} detail={data} config={config} members={members} onlyFields={[quickField]} />}
      {addingTeam && <EntityForm open onOpenChange={setAddingTeam} type="assignment" accountId={account.id} detail={data} config={config} members={members} />}
      {statusOpen && <StatusDialog accountId={account.id} current={account.status} onClose={() => setStatusOpen(false)} />}
    </div>
  );
}

function SummaryBlock({ title, items, onEdit }: { title: string; items: string[]; onEdit?: () => void }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{title}</CardTitle>
        {onEdit && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" aria-label={t('oml.detail.edit_block', 'Ver y editar {{block}}', { block: title })} onClick={onEdit}><Pencil className="h-3.5 w-3.5" /></Button>}
      </CardHeader>
      <CardContent className="text-sm">
        {items.length ? <ul className="list-disc pl-5 space-y-0.5">{items.map((x, i) => <li key={i}>{x}</li>)}</ul> : <p className="text-muted-foreground">{t('oml.detail.nothing_yet', 'Todavía no hay nada cargado.')}</p>}
      </CardContent>
    </Card>
  );
}

/** Way of Working (§6.1, AC-32): procesos ordenados por Orden con responsable y última revisión. */
function WayOfWorking({ rows, detail, config, members, names, showArchived }: { rows: (Values & { id: string; locked: string[]; archived?: boolean })[]; detail: NonNullable<ReturnType<typeof useOmlAccount>['data']>; config: ReturnType<typeof useOmlConfig>['data']; members: { email: string; name: string }[]; names: Record<string, string>; showArchived: boolean }) {
  const { t } = useTranslation();
  const sorted = [...rows].filter((r) => showArchived || !r.archived).sort((a, b) => (Number(a.order ?? 999) - Number(b.order ?? 999)) || String(a.processType).localeCompare(String(b.processType)));
  const canEdit = !!detail.access.entities.process?.edit;
  const [editingProcess, setEditingProcess] = useState<(Values & { id: string; locked: string[] }) | null>(null);
  return (
    <div className="space-y-4">
      {sorted.map((p) => (
        <Card key={p.id} className={p.archived ? 'opacity-60' : ''}>
          <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
            <CardTitle className="text-base">{String(p.processType)}</CardTitle>
            <div className="flex items-start gap-1">
              <div className="text-xs text-muted-foreground text-right">{t('oml.wow.responsible', 'Responsable')}: {names[String(p.responsible)] ?? String(p.responsible)}<br />{t('oml.wow.reviewed', 'Última revisión')}: {String(p.lastReviewedAt ?? '').slice(0, 10) || '—'}</div>
              {canEdit && !p.archived && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" aria-label={t('common.edit', 'Editar')} onClick={() => setEditingProcess(p)}><Pencil className="h-3.5 w-3.5" /></Button>}
            </div>
          </CardHeader>
          <CardContent><MarkdownText>{String(p.content ?? '')}</MarkdownText></CardContent>
        </Card>
      ))}
      <EntityTable type="process" detail={detail} config={config} members={members} rows={rows} showArchived={showArchived} />
      {editingProcess && <EntityForm open onOpenChange={(o) => { if (!o) setEditingProcess(null); }} type="process" accountId={detail.account.id} entity={editingProcess as Values & { id: string; locked: string[]; fieldVersions: Record<string, string>; updatedAt: string }} detail={detail} config={config} members={members} />}
    </div>
  );
}

function ArchiveAccountButton({ accountId, name }: { accountId: string; name: string }) {
  const { t } = useTranslation();
  const m = useOmlMutations();
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState('');
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}><Archive className="h-4 w-4 mr-1" />{t('oml.table.archive', 'Archivar')}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('oml.detail.archive_title', 'Archivar {{name}}', { name })}</DialogTitle>
            <DialogDescription>{t('oml.detail.archive_desc', 'Sale de todas las vistas y arrastra TODO su contenido: marcas, stakeholders, plataformas, journeys, campañas, experimentos, proyectos, procesos, reglas, segmentos, etiquetas, links y reportes. Las asignaciones vigentes se cierran. Se puede restaurar y queda registrado.')}</DialogDescription>
          </DialogHeader>
          <Textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={2} placeholder={t('oml.form.reason_optional', 'Motivo (opcional, queda en el historial)')} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
            <Button variant="destructive" disabled={m.archiveAccount.isPending} onClick={() => m.archiveAccount.mutate({ accountId, justification: why || undefined }, { onSuccess: () => setOpen(false) })}>{t('oml.table.archive', 'Archivar')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Cambio de estado de la cuenta según la máquina 13.2; cerrar exige justificación (§8.5). */
function StatusDialog({ accountId, current, onClose }: { accountId: string; current: string; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useOmlMutations();
  const options = (OML_TRANSITIONS.account?.[current] ?? []).map((x) => x.to).filter((x) => x !== ACCOUNT_STATUS.ARCHIVED);
  const [to, setTo] = useState(options[0] ?? '');
  const [why, setWhy] = useState('');
  const needsWhy = to === ACCOUNT_STATUS.CLOSED;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('oml.detail.change_status', 'Cambiar estado')}</DialogTitle>
          <DialogDescription>{t('oml.detail.status_desc', 'Desde "{{from}}" se puede pasar a los estados de abajo (§13.2). Cerrar exige que no queden asignaciones vigentes y una justificación.', { from: current })}</DialogDescription>
        </DialogHeader>
        <Select value={to} onValueChange={setTo}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        {needsWhy && <Textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={2} placeholder={t('oml.form.justification', 'Justificación (obligatoria)')} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel', 'Cancelar')}</Button>
          <Button disabled={!to || (needsWhy && why.trim().length < 3) || m.updateAccount.isPending} onClick={() => m.updateAccount.mutate({ accountId, patch: { status: to }, touched: ['status'], justification: why || undefined }, { onSuccess: onClose, onError: (e) => { const d = (e as { data?: { oml?: { issues?: { message: string }[] } } }).data?.oml; if (d?.issues?.length) alert(d.issues.map((i) => i.message).join('\n')); } })}>{t('common.confirm', 'Confirmar')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
